import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useOutletContext } from 'react-router';
import { api, unwrap } from '../api/client';
import { useServices } from '../api/hooks';
import { ErrorNote, Loading, PageHead, formatDateTime } from '../components/ui';

interface MeContext {
  roles: Array<{ role: string }>;
}

const ACTOR_LABELS: Record<string, string> = {
  n1: "Chef du service de l'agent (N+1)",
  n2: 'Chef du niveau au-dessus (N+2)',
  drh: 'Direction des ressources humaines',
  service_head: "Chef d'un service précis",
  role: 'Tout titulaire d’un rôle',
};

type StepDraft = { code: string; label: string; actorKind: string; actorServiceId: string; actorRole: string };
type GuardDraft = { guardType: string; enabled: boolean; blocking: boolean; params: Record<string, string> };

export function WorkflowsPage() {
  const me = useOutletContext<MeContext>();
  const isDrh = me.roles.some((r) => r.role === 'drh');
  const qc = useQueryClient();
  const defs = useQuery({ queryKey: ['workflows'], queryFn: async () => unwrap(await api.GET('/api/v1/workflows/definitions')) });
  const catalog = useQuery({ queryKey: ['workflows', 'guards'], queryFn: async () => unwrap(await api.GET('/api/v1/workflows/guards')), staleTime: Infinity });
  const services = useServices();
  const serviceName = (id: string | null) => (id ? (services.data?.find((s) => s.id === id)?.name ?? '…') : 'Toute la collectivité');

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [scope, setScope] = useState('');
  const [steps, setSteps] = useState<StepDraft[]>([{ code: 'N1', label: 'Validation du chef de service', actorKind: 'n1', actorServiceId: '', actorRole: '' }]);
  const [guards, setGuards] = useState<GuardDraft[] | null>(null);

  const guardDrafts = guards ?? (catalog.data ?? []).map((g) => ({
    guardType: g.type,
    enabled: false,
    blocking: true,
    params: Object.fromEntries(Object.entries(g.defaultParams).map(([k, v]) => [k, String(v)])),
  }));

  const publish = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/workflows/definitions', {
          body: {
            requestType: 'leave',
            name,
            serviceId: scope || null,
            steps: steps.map((s) => ({
              code: s.code,
              label: s.label,
              actorKind: s.actorKind as 'n1',
              actorServiceId: s.actorKind === 'service_head' ? s.actorServiceId || null : null,
              actorRole: s.actorKind === 'role' ? s.actorRole || null : null,
            })),
            guards: guardDrafts
              .filter((g) => g.enabled)
              .map((g) => ({
                guardType: g.guardType as 'notice_days',
                blocking: g.blocking,
                params: Object.fromEntries(Object.entries(g.params).map(([k, v]) => [k, Number.isFinite(Number(v)) && v !== '' ? Number(v) : v])),
              })),
          },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workflows'] });
      setOpen(false);
      setName('');
      setGuards(null);
    },
  });

  const updateStep = (i: number, patch: Partial<StepDraft>) => setSteps((s) => s.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const updateGuard = (type: string, patch: Partial<GuardDraft>) => setGuards(guardDrafts.map((g) => (g.guardType === type ? { ...g, ...patch } : g)));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    publish.mutate();
  };

  const active = (defs.data ?? []).filter((d) => d.status === 'active');
  const retired = (defs.data ?? []).filter((d) => d.status !== 'active');

  return (
    <>
      <PageHead
        eyebrow="Paramétrage"
        title="Circuits de validation"
        actions={
          isDrh && (
            <button className="btn btn-primary" onClick={() => setOpen((o) => !o)}>
              {open ? 'Fermer' : 'Nouveau circuit'}
            </button>
          )
        }
      >
        Un circuit par défaut pour la collectivité, et des circuits propres à un service et ses sous-services. Publier remplace la version active du même périmètre ; les demandes en cours terminent sur leur version.
      </PageHead>

      {open && isDrh && (
        <form className="card stack" onSubmit={submit} style={{ marginBottom: 24 }}>
          <h2>Publier un circuit de congé</h2>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="w-name">Nom</label>
              <input id="w-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Congés · services techniques" />
            </div>
            <div className="field">
              <label htmlFor="w-scope">Périmètre</label>
              <select id="w-scope" value={scope} onChange={(e) => setScope(e.target.value)}>
                <option value="">Toute la collectivité (circuit par défaut)</option>
                {(services.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} · {s.name} et ses sous-services
                  </option>
                ))}
              </select>
            </div>
          </div>

          <h3>Étapes, dans l'ordre</h3>
          {steps.map((s, i) => (
            <div key={i} className="form-grid" style={{ alignItems: 'end' }}>
              <div className="field">
                <label htmlFor={`s-label-${i}`}>Étape {i + 1}</label>
                <input id={`s-label-${i}`} required value={s.label} onChange={(e) => updateStep(i, { label: e.target.value, code: e.target.value.slice(0, 3).toUpperCase() + (i + 1) })} />
              </div>
              <div className="field">
                <label htmlFor={`s-actor-${i}`}>Qui décide</label>
                <select id={`s-actor-${i}`} value={s.actorKind} onChange={(e) => updateStep(i, { actorKind: e.target.value })}>
                  {Object.entries(ACTOR_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              {s.actorKind === 'service_head' && (
                <div className="field">
                  <label htmlFor={`s-svc-${i}`}>Service</label>
                  <select id={`s-svc-${i}`} required value={s.actorServiceId} onChange={(e) => updateStep(i, { actorServiceId: e.target.value })}>
                    <option value="">— Choisir —</option>
                    {(services.data ?? []).map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.code} · {x.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {s.actorKind === 'role' && (
                <div className="field">
                  <label htmlFor={`s-role-${i}`}>Rôle</label>
                  <select id={`s-role-${i}`} required value={s.actorRole} onChange={(e) => updateStep(i, { actorRole: e.target.value })}>
                    <option value="">— Choisir —</option>
                    <option value="drh">DRH</option>
                    <option value="elu">Élu</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
              )}
              <div className="field">
                <button type="button" className="btn btn-sm" disabled={steps.length === 1} onClick={() => setSteps((x) => x.filter((_, j) => j !== i))}>
                  Retirer
                </button>
              </div>
            </div>
          ))}
          <div>
            <button type="button" className="btn btn-sm" onClick={() => setSteps((x) => [...x, { code: `E${x.length + 1}`, label: '', actorKind: 'drh', actorServiceId: '', actorRole: '' }])}>
              Ajouter une étape
            </button>
          </div>

          <h3>Garde-fous</h3>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Actif</th>
                  <th>Règle</th>
                  <th>Paramètres</th>
                  <th>Effet</th>
                </tr>
              </thead>
              <tbody>
                {guardDrafts.map((g) => {
                  const spec = catalog.data?.find((c) => c.type === g.guardType);
                  return (
                    <tr key={g.guardType}>
                      <td>
                        <input type="checkbox" id={`g-${g.guardType}`} checked={g.enabled} onChange={(e) => updateGuard(g.guardType, { enabled: e.target.checked })} aria-label={spec?.label} />
                      </td>
                      <td>
                        <strong>{spec?.label}</strong>
                        <div className="small muted">{spec?.description}</div>
                      </td>
                      <td>
                        {Object.entries(g.params).map(([k, v]) => (
                          <label key={k} className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            {k}
                            <input type="number" min={0} value={v} style={{ width: 80 }} onChange={(e) => updateGuard(g.guardType, { params: { ...g.params, [k]: e.target.value } })} />
                          </label>
                        ))}
                        {Object.keys(g.params).length === 0 && <span className="muted small">—</span>}
                      </td>
                      <td>
                        <select value={g.blocking ? 'block' : 'warn'} onChange={(e) => updateGuard(g.guardType, { blocking: e.target.value === 'block' })} aria-label="Effet">
                          <option value="block">Bloque la demande</option>
                          <option value="warn">Avertit le valideur</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ErrorNote error={publish.error} />
          <div className="actions">
            <button type="submit" className="btn btn-primary" disabled={publish.isPending}>
              Publier cette version
            </button>
          </div>
        </form>
      )}

      <ErrorNote error={defs.error} />
      {defs.isPending ? (
        <Loading />
      ) : (
        <div className="stack">
          {active.map((d) => (
            <section key={d.id} className="card">
              <div className="page-head" style={{ marginBottom: 8 }}>
                <div>
                  <div className="eyebrow">{serviceName(d.serviceId)}</div>
                  <h2>
                    {d.name} <span className="b b-ok">v{d.version} · active</span>
                  </h2>
                </div>
                <span className="muted small">publiée le {formatDateTime(d.createdAt)}</span>
              </div>
              <ol>
                {d.steps.map((s) => (
                  <li key={s.id}>
                    <strong>{s.label}</strong> <span className="muted">· {ACTOR_LABELS[s.actorKind] ?? s.actorKind}</span>
                    {s.actorServiceId && <span className="muted"> ({serviceName(s.actorServiceId)})</span>}
                    {s.actorRole && <span className="muted"> ({s.actorRole})</span>}
                  </li>
                ))}
              </ol>
              <div className="small">
                {d.guards.length === 0 ? (
                  <span className="muted">Aucun garde-fou.</span>
                ) : (
                  d.guards.map((g) => (
                    <span key={g.id} className={`b ${g.blocking ? 'b-bad' : 'b-warn'}`} style={{ marginRight: 6 }}>
                      {catalog.data?.find((c) => c.type === g.guardType)?.label ?? g.guardType} {Object.values(g.params as Record<string, unknown>).join(' ')}
                    </span>
                  ))
                )}
              </div>
            </section>
          ))}
          {retired.length > 0 && (
            <details>
              <summary className="muted">{retired.length} version(s) retirée(s)</summary>
              <ul>
                {retired.map((d) => (
                  <li key={d.id} className="small muted">
                    {serviceName(d.serviceId)} · {d.name} v{d.version}, retirée le {formatDateTime(d.retiredAt)}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </>
  );
}
